import { useState, useEffect } from 'react';
import { X, Plus, Edit2 } from 'lucide-react';
import { Link } from "react-router-dom";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Command, CommandGroup, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { format } from 'date-fns';
import { CreateConnectionDialog } from './CreateConnectionDialog';
import { captureError } from '@/lib/sentry';

interface Connection {
  id: string;
  contact_id_1: string;
  contact_id_2: string;
  relationship_type: string;
  created_at: string;
  contact: {
    id: string;
    first_name: string;
    last_name: string;
    company_name?: string;
  };
}

interface ContactConnectionsTabProps {
  contactId: string;
}

export function ContactConnectionsTab({ contactId }: ContactConnectionsTabProps) {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingConnectionId, setEditingConnectionId] = useState<string | null>(null);
  const [relationshipTypes, setRelationshipTypes] = useState<string[]>([]);
  const [editedRelationshipType, setEditedRelationshipType] = useState<string>("");
  const [customType, setCustomType] = useState<string>("");
  const [showTypePopover, setShowTypePopover] = useState<boolean>(false);
  const { toast } = useToast();
  const { currentWorkspace } = useWorkspaces();

  const fetchConnections = async () => {
    try {
      // Fetch connections where the current contact is either contact_1 or contact_2
      const { data: connectionsData, error: contactsError } = await supabase
        .from('connections')
        .select(`
          id,
          contact_id_1,
          contact_id_2,
          relationship_type,
          created_at,
          contact:contacts!connections_contact_id_2_fkey (
            id,
            first_name,
            last_name,
            company_name
          )
        `)
        .eq('contact_id_1', contactId);

      if (contactsError) throw contactsError;

      // Also fetch reverse connections
      const { data: reverseData, error: reverseError } = await supabase
        .from('connections')
        .select(`
          id,
          contact_id_1,
          contact_id_2,
          relationship_type,
          created_at,
          contact:contacts!connections_contact_id_1_fkey (
            id,
            first_name,
            last_name,
            company_name
          )
        `)
        .eq('contact_id_2', contactId);

      if (reverseError) throw reverseError;

      const allConnections = [...(connectionsData || []), ...(reverseData || [])].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setConnections(allConnections as unknown as Connection[]);
    } catch (error) {
      console.error('Error fetching connections:', error);
      captureError(error as Error, { feature: 'contacts-contact-connections-tab-fetch-connections' });
      toast({
        title: "Error",
        description: "Failed to load connections",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRelationshipTypes = async () => {
    try {
      // Get unique relationship types from existing connections
      const { data: connectionsData, error: connectionsError } = await supabase
        .from('connections')
        .select('relationship_type')
        .not('relationship_type', 'is', null);

      if (connectionsError) throw connectionsError;

      const connectionTypes = (connectionsData || []).map(c => c.relationship_type);
      
      const allTypes = [...new Set([...connectionTypes])]
        .filter(type => type && type.trim())
        .sort();

      console.log('Found relationship types:', allTypes);
      
      setRelationshipTypes(allTypes);
    } catch (error) {
      console.error('Error fetching relationship types:', error);
    }
  };

  useEffect(() => {
    if (currentWorkspace) {
      console.log('Fetching relationship types');
      fetchRelationshipTypes();
    }
  }, [currentWorkspace?.id]);

  useEffect(() => {
    fetchConnections();
    fetchRelationshipTypes();
  }, [contactId]);

  const removeConnection = async (connectionId: string) => {
    try {
      const { error } = await supabase
        .from('connections')
        .delete()
        .eq('id', connectionId);

      if (error) throw error;

      toast({
        description: "Connection removed successfully",
      });

      fetchConnections();
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-connections-tab-remove-connection' });
      toast({
        title: "Error",
        description: "Failed to remove connection",
        variant: "destructive",
      });
    }
  };

  const updateConnectionType = async (connectionId: string) => {
    let typeToSave = editedRelationshipType;
    
    // If custom type is provided, use that instead
    if (customType.trim()) {
      typeToSave = customType.trim();
    }
    
    if (!typeToSave) return;
    
    try {
      const { error } = await supabase
        .from('connections')
        .update({ relationship_type: typeToSave })
        .eq('id', connectionId);

      if (error) throw error;

      // Add to relationship types if it's a new type
      if (!relationshipTypes.includes(typeToSave.toLowerCase())) {
        setRelationshipTypes(prev => [...prev, typeToSave.toLowerCase()]);
      }

      setEditingConnectionId(null);
      setCustomType("");
      await fetchConnections();
      
      toast({
        description: "Connection type updated successfully",
      });
    } catch (error) {
      console.error('Error updating connection:', error);
      captureError(error as Error, { feature: 'contacts-contact-connections-tab-update-connection-type' });
      toast({
        title: "Error",
        description: "Failed to update connection",
        variant: "destructive",
      });
    }
  };

  const handleEditClick = (connection: Connection) => {
    setEditingConnectionId(connection.id);
    setEditedRelationshipType(connection.relationship_type || "");
    setCustomType("");
  };

  if (loading) {
    return <div className="py-2 text-center text-muted-foreground text-sm">Loading connections...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="font-semibold text-sm">Connections</h2>
        <Button 
          size="sm" 
          variant="outline" 
          onClick={() => setCreateDialogOpen(true)}
          className="h-7 text-xs"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Connection
        </Button>
      </div>

      <div className="space-y-2">
        {connections.map((connection) => {
          // The two fetch queries above join on different FKs so `connection.contact`
          // already resolves to the contact on the opposite side regardless of direction.
          const connectedContact = connection.contact;
          const personName = [connectedContact.first_name, connectedContact.last_name]
            .filter((p) => p && p.trim())
            .join(' ')
            .trim();
          const linkLabel = personName || connectedContact.company_name || 'Unnamed contact';
          // Avoid duplicating the company name below the link if it's already the link text.
          const showCompanySubline = connectedContact.company_name && connectedContact.company_name !== linkLabel;

          return (
            <div
              key={connection.id}
              className="group flex items-center justify-between p-3 rounded-lg border hover:bg-accent/5 transition-colors"
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Link
                    to={`/contacts/${connectedContact.id}`}
                    className="text-sm font-medium hover:underline"
                  >
                    {linkLabel}
                  </Link>
                  {editingConnectionId === connection.id ? (
                    <div className="flex items-center gap-2">
                      {customType ? (
                        <Input
                          value={customType}
                          onChange={(e) => setCustomType(e.target.value)}
                          placeholder="Enter relationship type"
                          className="h-7 text-xs w-[180px]"
                        />
                      ) : (
                        <Popover open={showTypePopover} onOpenChange={setShowTypePopover}>
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="h-7 text-xs w-[180px] justify-between">
                              {editedRelationshipType || "Select type"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="p-0 w-[220px]" align="start">
                            <Command>
                              <CommandGroup>
                                {relationshipTypes.map((type) => (
                                  <CommandItem
                                    key={type}
                                    onSelect={() => {
                                      setEditedRelationshipType(type);
                                      setCustomType("");
                                      setShowTypePopover(false);
                                    }}
                                    className="text-xs capitalize cursor-pointer"
                                  >
                                    {type}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      )}
                      
                      {!customType && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => {
                            setCustomType(" "); // Use space to trigger custom input display
                            // Focus on custom input
                            setTimeout(() => {
                              const customInput = document.getElementById("custom-type-input");
                              if (customInput) {
                                customInput.focus();
                              }
                            }, 0);
                          }}
                          className="h-7 text-xs"
                        >
                          Custom
                        </Button>
                      )}
                      
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => updateConnectionType(connection.id)}
                        className="h-7 text-xs"
                        disabled={!editedRelationshipType && !customType.trim()}
                      >
                        Save
                      </Button>
                      <Button
                        variant="ghost" 
                        size="sm"
                        onClick={() => setEditingConnectionId(null)}
                        className="h-7 text-xs"
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Badge variant="outline" className="text-xs capitalize">
                      {connection.relationship_type || "No type"}
                    </Badge>
                  )}
                </div>
                
                {/* Company name — only show if it isn't already the link label */}
                {showCompanySubline && (
                  <div className="text-xs text-muted-foreground">
                    {connectedContact.company_name}
                  </div>
                )}
                
                <div className="text-xs text-muted-foreground">
                  Connected {format(new Date(connection.created_at), 'PPp')}
                </div>
              </div>

              {editingConnectionId !== connection.id && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEditClick(connection)}
                    className="h-6 w-6"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeConnection(connection.id)}
                    className="h-6 w-6 text-destructive"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
          );
        })}

        {connections.length === 0 && (
          <div className="text-center py-4 text-xs text-muted-foreground">
            No connections yet. Add your first connection above.
          </div>
        )}
      </div>

      <CreateConnectionDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        contactId={contactId}
        onSuccess={fetchConnections}
      />
    </div>
  );
}